import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { IncidentService } from './service'
import {
  AddIncident,
  AddIncidentComplete,
  ArchiveIncidents,
  ArchiveIncidentsComplete,
  DeleteIncident,
  DeleteIncidentComplete,
  DeleteIncidents,
  DeleteIncidentsComplete,
  LoadIncidents,
  LoadIncidentsByIds,
  LoadIncidentsByIdsComplete,
  LoadIncidentsComplete,
  UnarchiveIncidents,
  UnarchiveIncidentsComplete,
  UpdateIncident,
  UpdateIncidentComplete,
} from './actions'
import { ActionFailed, Toaster } from '@tokeet-frontend/tv3-platform'

@Injectable()
export class IncidentEffects {
  @Effect()
  loadIncidents$ = this.actions$.pipe(
    ofType(LoadIncidents),
    switchMap(({ params, pagination }) =>
      this.incidents.search(params, pagination).pipe(
        map((incidents) => LoadIncidentsComplete({ incidents })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadIncidentsByIds$ = this.actions$.pipe(
    ofType(LoadIncidentsByIds),
    switchMap(({ ids }) =>
      this.incidents.getByIds(ids).pipe(
        map((incidents) => LoadIncidentsByIdsComplete({ incidents })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addIncident$ = this.actions$.pipe(
    ofType(AddIncident),
    switchMap(({ payload }) =>
      this.incidents.create(payload).pipe(
        map((incident) => AddIncidentComplete({ incident })),

        tap(() => this.toast.success($localize`Incident created successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateIncident$ = this.actions$.pipe(
    ofType(UpdateIncident),
    switchMap(({ payload, incidentId }) =>
      this.incidents.update(incidentId, payload).pipe(
        map((incident) => UpdateIncidentComplete({ update: { id: incidentId, changes: incident } })),

        tap(() => this.toast.success($localize`Incident updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveIncidents$ = this.actions$.pipe(
    ofType(ArchiveIncidents),
    switchMap(({ ids }) =>
      this.incidents.archiveIncidents(ids).pipe(
        tap(() =>
          this.toast.success(
            ids.length > 1 ? $localize`Incidents archived successfully.` : $localize`Incident archived successfully.`
          )
        ),
        map(() => ArchiveIncidentsComplete({ updates: ids.map((id) => ({ id, changes: { archived: 1 } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unarchiveIncidents$ = this.actions$.pipe(
    ofType(UnarchiveIncidents),
    switchMap(({ ids }) =>
      this.incidents.unarchiveIncidents(ids).pipe(
        tap(() =>
          this.toast.success(
            ids.length > 1
              ? $localize`Incidents unarchived successfully.`
              : $localize`Incident unarchived successfully.`
          )
        ),
        map(() => UnarchiveIncidentsComplete({ updates: ids.map((id) => ({ id, changes: { archived: 0 } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteIncident$ = this.actions$.pipe(
    ofType(DeleteIncident),
    switchMap(({ id }) =>
      this.incidents.delete(id).pipe(
        map((incident) => DeleteIncidentComplete({ id: incident.pkey })),
        tap(() => this.toast.success($localize`Incident deleted successfully.`)),

        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteIncidents$ = this.actions$.pipe(
    ofType(DeleteIncidents),
    concatMap(({ ids }) =>
      this.incidents.deleteIncidents(ids).pipe(
        map(() => DeleteIncidentsComplete({ ids })),
        tap(() => this.toast.success($localize`Incidents deleted successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(private actions$: Actions, private incidents: IncidentService, private toast: Toaster) {}
}
